export const SCREENING_STATUS = Object.freeze({
  CLEAR: "CLEAR",
  SOFT_MATCH: "SOFT MATCH",
  POTENTIAL_MATCH: "POTENTIAL MATCH",
  UNAVAILABLE: "UNAVAILABLE",
});

export const SCREENING_DESCRIPTIONS = Object.freeze({
  SOFT_MATCH:
    "Soft Match indicates that the guest's name and date of birth match information found in the database, but no qualifying conviction or confirmed record was identified. This result is provided for transparency and indicates that no verified record match was found.",
  CONVICTION_POTENTIAL_MATCH:
    "Potential Match indicates that the guest's name and date of birth match the identified record, and a qualifying conviction record was found.",
  OFAC_POTENTIAL_MATCH:
    "OFAC maintains U.S. sanctions lists of restricted individuals and entities. A Potential Match means the guest's name and date of birth match an OFAC-listed record.",
  UN_POTENTIAL_MATCH:
    "The UN maintains sanctions lists of individuals, organizations, and entities subject to international restrictions. A Potential Match means the guest's name and date of birth match a listed individual or entity.",
});

const STATUS_PRIORITY = [
  SCREENING_STATUS.POTENTIAL_MATCH,
  SCREENING_STATUS.SOFT_MATCH,
  SCREENING_STATUS.UNAVAILABLE,
  SCREENING_STATUS.CLEAR,
];

const normalizeStatus = (value) => {
  const normalized = String(value || "").trim().replace(/\s+/g, " ").toUpperCase();
  return normalized === ["CONFIRMED", "MATCH"].join(" ")
    ? SCREENING_STATUS.POTENTIAL_MATCH
    : normalized;
};

export const resolveOverallScreeningStatus = (result = {}) => {
  const explicit = normalizeStatus(result.screeningResult);
  if (STATUS_PRIORITY.includes(explicit)) return explicit;

  const statuses = [
    result.criminalList,
    result.sexOffenderList,
    result.ofacList,
    result.unList,
    result.fbiList,
  ].map(normalizeStatus);

  return STATUS_PRIORITY.find((candidate) => statuses.includes(candidate)) || SCREENING_STATUS.CLEAR;
};

export const isReviewScreeningStatus = (value) => [
  SCREENING_STATUS.SOFT_MATCH,
  SCREENING_STATUS.POTENTIAL_MATCH,
].includes(normalizeStatus(value));

export const getScreeningStatusDisplay = (value) => {
  const status = normalizeStatus(value) || SCREENING_STATUS.CLEAR;
  if (status === SCREENING_STATUS.POTENTIAL_MATCH) {
    return { status, tone: "potential", icon: "pi-exclamation-triangle" };
  }
  if (status === SCREENING_STATUS.SOFT_MATCH) {
    return { status, tone: "soft", icon: "pi-info-circle" };
  }
  if (status === SCREENING_STATUS.UNAVAILABLE) {
    return { status, tone: "unavailable", icon: "pi-minus-circle" };
  }
  return { status: SCREENING_STATUS.CLEAR, tone: "clear", icon: "pi-check-circle" };
};

export const parseScreeningRecords = (records) => {
  if (Array.isArray(records)) return records;
  if (typeof records !== "string") return [];
  try {
    const parsed = JSON.parse(records);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const recordsForWatchlistSource = (records, sourceKey) =>
  parseScreeningRecords(records).filter((record) => record?.sourceKey === sourceKey);

const normalizeName = (value) => String(value || "")
  .normalize("NFKD")
  .replace(/[\u0300-\u036f]/g, "")
  .replace(/[^a-z0-9]/gi, "")
  .toLowerCase();

const normalizeDate = (value) => {
  const raw = String(value || "").trim();
  const iso = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (iso) return `${iso[1]}-${iso[2].padStart(2, "0")}-${iso[3].padStart(2, "0")}`;
  const us = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (!us) return "";
  let year = us[3];
  if (year.length === 2) year = Number(year) >= 70 ? `19${year}` : `20${year}`;
  return `${year}-${us[1].padStart(2, "0")}-${us[2].padStart(2, "0")}`;
};

const readPayloadValue = (payload, ...keys) => {
  for (const key of keys) {
    if (payload?.[key] !== undefined && payload[key] !== null) return payload[key];
  }
  return "";
};

const hasConviction = (record) => (Array.isArray(record?.offenses) ? record.offenses : []).some((offense) => {
  const disposition = String(offense?.disposition || offense?.Disposition || "").toUpperCase();
  const isExcluded = ["ACQUITTED", "DISMISSED", "NOT GUILTY", "NOT PROSECUTED", "NOLLE PROSEQUI", "WITHDRAWN"]
    .some((candidate) => disposition.includes(candidate));
  const isConvictionDisposition = !isExcluded && (
    disposition === "CON" ||
    disposition.startsWith("CONV") ||
    ["ADJUDICATED GUILTY", "CONVICTED", "FOUND GUILTY", "GUILTY", "PLEADED GUILTY", "PLEA OF GUILTY"]
      .some((candidate) => disposition.includes(candidate))
  );
  return Boolean(
    offense?.convictionDate ||
    offense?.ConvictionDate ||
    offense?.convictionPlace ||
    offense?.ConvictionPlace ||
    offense?.sentenced ||
    offense?.Sentenced ||
    offense?.probationDate ||
    offense?.ProbationDate ||
    isConvictionDisposition,
  );
});

export const normalizeLegacyEndatoResult = ({ status, records, payload }) => {
  const normalizedStatus = normalizeStatus(status);
  if ([
    SCREENING_STATUS.CLEAR,
    SCREENING_STATUS.SOFT_MATCH,
    SCREENING_STATUS.POTENTIAL_MATCH,
    SCREENING_STATUS.UNAVAILABLE,
  ].includes(normalizedStatus)) {
    return normalizedStatus;
  }
  if (normalizedStatus !== "NAME MATCH") return SCREENING_STATUS.CLEAR;

  const expectedFirstName = normalizeName(readPayloadValue(payload, "first_name", "firstName", "FIRST_NAME"));
  const expectedLastName = normalizeName(readPayloadValue(payload, "last_name", "lastName", "LAST_NAME"));
  const expectedDob = normalizeDate(readPayloadValue(payload, "dob", "birthDate", "DOB"));
  if (!expectedFirstName || !expectedLastName || !expectedDob) return SCREENING_STATUS.SOFT_MATCH;

  const exactMatches = parseScreeningRecords(records).filter((record) => {
    const nameMatches = (Array.isArray(record?.names) ? record.names : []).some((name) =>
      normalizeName(name?.firstName ?? name?.FirstName) === expectedFirstName &&
      normalizeName(name?.lastName ?? name?.LastName) === expectedLastName);
    const dobMatches = (Array.isArray(record?.offenderAttributes) ? record.offenderAttributes : []).some((attributes) =>
      normalizeDate(attributes?.dob ?? attributes?.Dob) === expectedDob);
    return nameMatches && dobMatches;
  });
  if (exactMatches.length === 0) return SCREENING_STATUS.CLEAR;
  return exactMatches.some(hasConviction)
    ? SCREENING_STATUS.POTENTIAL_MATCH
    : SCREENING_STATUS.SOFT_MATCH;
};
